import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import {
  Heart,
  Sparkles,
  Activity,
  HeartPulse,
  Smartphone,
  Watch,
  Stethoscope,
  Calendar,
  Moon,
  TrendingUp,
  Smile,
} from "lucide-react";

const ICON = {
  "member.joined": Heart,
  "conversation.first": Sparkles,
  "conversation.milestone": Sparkles,
  "digital_twin.first": Activity,
  "device.first_session": Smartphone,
  "device.handover.first": Smartphone,
  "wearable.first_connected": Watch,
  "lab.first_upload": HeartPulse,
  "lab.uploaded": HeartPulse,
  "vital.first_recorded": HeartPulse,
  "medication.first_added": Stethoscope,
  "allergy.first_added": Stethoscope,
  "condition.first_added": Stethoscope,
  "radiology.first": Stethoscope,
  "pathology.first": Stethoscope,
  "sleep.improvement": Moon,
  "steps.streak_7d": TrendingUp,
  "mood.streak_positive_7d": Smile,
  "anniversary.month": Calendar,
  "anniversary.year": Calendar,
  "goal.achieved": Sparkles,
  "milestone.custom": Sparkles,
};

const BAND_LABEL = {
  nascent: { tr: "Henüz tanışıyoruz", en: "Just getting to know each other" },
  growing: { tr: "Büyüyor", en: "Growing" },
  strong: { tr: "Güçlü", en: "Strong" },
  deep: { tr: "Derin", en: "Deep" },
};

const COMPONENT_LABEL = {
  longevity: { tr: "Süreklilik", en: "Longevity" },
  frequency: { tr: "Sıklık", en: "Frequency" },
  depth: { tr: "Derinlik", en: "Depth" },
  milestones: { tr: "Dönüm noktaları", en: "Milestones" },
};

export default function LifePage() {
  const { t, language } = useLanguage();
  const [score, setScore] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([api.get("/life/relationship-score"), api.get("/life/timeline")])
      .then(([s, t]) => {
        setScore(s.data);
        setEvents(t.data);
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  return (
    <div className="space-y-8" data-testid="life-page">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          ME Life Engine
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
          {language === "tr" ? "ME ile Yolculuğun" : "Your Journey with ME"}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">
          {language === "tr"
            ? "Her önemli an bir Yaşam Olayı olur. ME yalnızca veri saklamaz — birlikte büyüyor."
            : "Every meaningful moment becomes a Life Event. ME doesn't just store data — we grow together."}
        </p>
      </header>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {score && <RelationshipScoreCard score={score} language={language} />}

      <section className="mios-card" data-testid="life-timeline">
        <div className="mios-card-header">
          <div className="mios-card-title">
            {language === "tr" ? "Yaşam Olayları" : "Life Events"}
          </div>
          <div className="mios-card-sub">{events.length}</div>
        </div>
        <div className="mios-card-body">
          {events.length === 0 ? (
            <div className="text-sm text-slate-400">—</div>
          ) : (
            <ol className="relative space-y-5 border-l-2 border-slate-100 pl-6" data-testid="timeline-list">
              {events.map((ev) => {
                const Icon = ICON[ev.kind] || Sparkles;
                const title = language === "en" ? ev.title_en || ev.title : ev.title || ev.title_en;
                const date = (ev.occurred_at || "").slice(0, 10);
                const importance = ev.importance >= 8 ? "milestone" : ev.importance >= 6 ? "notable" : "moment";
                const pillCls =
                  importance === "milestone" ? "mios-pill-high" :
                  importance === "notable" ? "mios-pill-warning" :
                  "mios-pill-normal";
                return (
                  <li key={ev.id} className="relative" data-testid={`timeline-item-${ev.kind}`}>
                    <span className="absolute -left-[34px] flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-slate-900 text-white shadow-sm">
                      <Icon className="h-3.5 w-3.5" strokeWidth={2} />
                    </span>
                    <div className="flex flex-wrap items-baseline justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-slate-900">{title}</div>
                        <div className="text-[11px] text-slate-500 mono">{date} · {ev.kind}</div>
                      </div>
                      <span className={`mios-pill ${pillCls}`}>{importance}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </section>
    </div>
  );
}

function RelationshipScoreCard({ score, language }) {
  const band = BAND_LABEL[score.band] || BAND_LABEL.growing;
  return (
    <section className="mios-card" data-testid="relationship-score">
      <div className="mios-card-body">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="mios-card-sub">
              {language === "tr" ? "İlişki Puanı" : "Relationship Score"}
            </div>
            <div className="mt-1 flex items-baseline gap-3">
              <div className="text-6xl font-semibold tracking-tight text-slate-900" data-testid="relationship-score-value">
                {score.value}
              </div>
              <div className="text-lg text-slate-500">/100</div>
              <span className="mios-pill mios-pill-normal" data-testid={`relationship-band-${score.band}`}>
                {band[language] || band.tr}
              </span>
            </div>
            <div className="mt-2 text-xs text-slate-500">
              {language === "tr"
                ? `${score.days_together} gündür birlikteyiz · ${score.events_total} yaşam olayı · ${score.milestones_total} dönüm noktası`
                : `${score.days_together} days together · ${score.events_total} life events · ${score.milestones_total} milestones`}
            </div>
          </div>
        </div>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="score-components">
          {["longevity", "frequency", "depth", "milestones"].map((k) => {
            const v = score.components[k];
            const label = COMPONENT_LABEL[k][language] || COMPONENT_LABEL[k].tr;
            return (
              <div key={k} className="rounded-lg border border-slate-200 bg-white px-3 py-3" data-testid={`component-${k}`}>
                <div className="mios-card-sub">{label}</div>
                <div className="mt-1 flex items-baseline gap-1.5">
                  <div className="mono text-2xl font-semibold text-slate-900">{v}</div>
                  <div className="text-xs text-slate-500">/25</div>
                </div>
                <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full bg-[color:var(--mios-brand)]"
                    style={{ width: `${(v / 25) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
